import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  RECORD_RESULT_LABELS,
  type BuildingDto,
  type MetrelImportBody,
  type MetrelImportResult,
  type MetrelPlanResponse,
  type MetrelRowStatus,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { ElementPicker } from "../elements/ElementPicker";
import { fmtDay } from "../obligations/ObligationSection";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

const STATUS_LABELS: Record<MetrelRowStatus, string> = {
  new: "Nieuw arbeidsmiddel",
  add: "Keuring erbij",
  done: "Al geïmporteerd",
  untested: "Niet getest",
  error: "Fout",
};
/** Wat aandacht nodig heeft eerst. */
const ORDER: MetrelRowStatus[] = ["error", "new", "add", "untested", "done"];

/**
 * Venster "Inlezen Metrel-keuringen" (docs/ARBEIDSMIDDELEN.md): het .padfx-project uit Metrel ES Manager.
 * Bekende stickers krijgen een keuring erbij, onbekende worden een nieuw arbeidsmiddel. Er wordt pas bij
 * "Importeren" iets opgeslagen.
 */
export function MetrelImportDialog({
  building: b,
  file,
  name,
  full,
  onClose,
}: {
  building: BuildingDto;
  file: string;
  name: string;
  full: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const cache = useBuildingCache(b.id);
  const plan = useQuery({
    queryKey: ["metrel-plan", b.id, name, file.length],
    queryFn: () =>
      api<MetrelPlanResponse>(`/buildings/${b.id}/assets/import/metrel/plan`, { method: "POST", body: { file } }),
    retry: false,
    gcTime: 0,
  });
  const p = plan.data;
  const [codes, setCodes] = useState<Record<string, string | null>>({});
  useEffect(() => {
    if (p) setCodes(Object.fromEntries(p.rows.filter((r) => r.status === "new").map((r) => [r.applianceId, r.code])));
  }, [p]);

  const counts = useMemo(() => {
    const c: Record<MetrelRowStatus, number> = { new: 0, add: 0, done: 0, untested: 0, error: 0 };
    for (const r of p?.rows ?? []) c[r.status]++;
    return c;
  }, [p]);
  const rejected = p?.rows.filter((r) => r.result === "afgekeurd" && (r.status === "new" || r.status === "add")) ?? [];
  const rows = useMemo(
    () => [...(p?.rows ?? [])].sort((a, c) => ORDER.indexOf(a.status) - ORDER.indexOf(c.status)),
    [p],
  );
  const total = counts.new + counts.add;

  const run = useMutation({
    mutationFn: () => {
      const body: MetrelImportBody = {
        file,
        fileName: name,
        codes: Object.entries(codes).map(([applianceId, code]) => ({ applianceId, code })),
      };
      return api<MetrelImportResult>(`/buildings/${b.id}/assets/import/metrel`, { method: "POST", body });
    },
    onSuccess: (r) => {
      void cache.refresh();
      toast(
        `Metrel ingelezen: ${r.records.created} ${r.records.created === 1 ? "keuring" : "keuringen"}` +
          (r.assetsCreated ? `, ${r.assetsCreated} nieuwe arbeidsmiddelen` : "") +
          (r.rejected ? `, ${r.rejected} afgekeurd` : "") +
          ".",
      );
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });

  const ins = p?.instrument;
  return (
    <Modal
      title="Inlezen Metrel-keuringen"
      onClose={onClose}
      wide
      busy={run.isPending}
      footer={
        <>
          {p && (
            <span className="muted small al-foot" aria-live="polite" data-testid="metrel-counts">
              {counts.new} nieuw · {counts.add} keuring erbij
              {counts.done ? ` · ${counts.done} al geïmporteerd` : ""}
              {counts.untested ? ` · ${counts.untested} niet getest` : ""}
              {counts.error ? ` · ${counts.error} fout` : ""}
            </span>
          )}
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!p || total === 0 || run.isPending}
            onClick={() => run.mutate()}
            data-testid="metrel-run"
          >
            {run.isPending ? "Bezig…" : `Importeren (${total})`}
          </button>
        </>
      }
    >
      <p className="muted small">{name}</p>
      {plan.isPending ? (
        <p className="muted">Bestand lezen…</p>
      ) : plan.error ? (
        <p role="alert" className="lib-warn">
          {errMsg(plan.error)}
        </p>
      ) : p ? (
        <div className="stack al-import">
          <p data-testid="metrel-summary">
            {p.rows.length} {p.rows.length === 1 ? "apparaat" : "apparaten"}
            {ins ? ` · gemeten met ${[ins.name, ins.model].filter(Boolean).join(" ")}` : ""}
            {ins?.serial ? ` (serienummer ${ins.serial}` : ""}
            {ins?.serial ? (ins.calibratedOn ? `, gekalibreerd ${fmtDay(ins.calibratedOn)})` : ")") : ""}. Er is nog
            niets opgeslagen.
          </p>
          {rejected.length > 0 && (
            <p className="lib-warn" role="status">
              {rejected.length} {rejected.length === 1 ? "arbeidsmiddel is" : "arbeidsmiddelen zijn"} afgekeurd en
              krijgen de status <strong>Afgekeurd, buiten gebruik</strong>.
            </p>
          )}
          {counts.new > 0 && (
            <p className="muted small">
              Nieuwe arbeidsmiddelen krijgen de stickercode als QR-code en geen plek op de tekening. Controleer de
              indeling (NL-SfB groep 8, losse inrichting).
            </p>
          )}
          <ul className="al-list" data-testid="metrel-rows">
            {rows.map((r) => (
              <li key={r.applianceId} className={`al-item al-prod${r.status === "error" ? " al-attn" : ""}`}>
                <div className="al-src">
                  <strong>{r.name}</strong>
                  <span className="muted small block">
                    <span className="code">{r.applianceId}</span>
                    {r.asset ? ` · ${r.asset.code}` : ""}
                    {r.procedure ? ` · ${r.procedure}` : ""}
                    {r.testedOn ? ` · ${fmtDay(r.testedOn)}` : ""}
                    {r.result ? ` · ${RECORD_RESULT_LABELS[r.result]}` : ""}
                  </span>
                  <span className={`chip small${r.status === "error" ? " lib-warn" : ""}`}>
                    {STATUS_LABELS[r.status]}
                    {r.message && r.status === "error" ? `: ${r.message}` : ""}
                  </span>
                </div>
                {r.status === "new" && (
                  <div className="al-picker">
                    <ElementPicker
                      value={codes[r.applianceId] ?? null}
                      full={full}
                      onChange={(code) => setCodes((s) => ({ ...s, [r.applianceId]: code }))}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
          {p.problems.length > 0 && (
            <p className="muted small">
              {p.problems.length} {p.problems.length === 1 ? "apparaat" : "apparaten"} zonder Appliance ID overgeslagen.
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
